/**
 * Rule evaluation utilities for dynamic cohort filtering
 * 
 * This module provides reusable functions to evaluate rule operators
 * against member field values, making it easy to add new fields without
 * duplicating operator logic.
 */

/**
 * Evaluates a rule operator against a member value
 * 
 * @param operator - The operator to apply (IS, IS_NOT, IN, NOT_IN, =, !=, <, <=, >, >=, BETWEEN, CONTAINS, NOT_CONTAINS)
 * @param memberValue - The value from the member object (can be string, number, or array)
 * @param ruleValue - The value from the rule (can be string, number, or array depending on operator)
 * @returns boolean indicating if the rule matches
 */
export function evaluateOperator(
  operator: string,
  memberValue: any,
  ruleValue: any
): boolean {
  // Normalize values for comparison
  const memberVal = memberValue != null ? memberValue : '';
  const ruleVal = ruleValue != null ? ruleValue : '';

  switch (operator) {
    // String/Select operators
    case 'IS':
      return String(memberVal) === String(ruleVal);
    
    case 'IS_NOT':
      return String(memberVal) !== String(ruleVal);
    
    case 'IN':
      if (!Array.isArray(ruleVal)) return false;
      return ruleVal.includes(memberVal) || ruleVal.map(String).includes(String(memberVal));
    
    case 'NOT_IN':
      if (!Array.isArray(ruleVal)) return false;
      return !ruleVal.includes(memberVal) && !ruleVal.map(String).includes(String(memberVal));
    
    case 'CONTAINS':
      return String(memberVal).toLowerCase().includes(String(ruleVal).toLowerCase());
    
    case 'NOT_CONTAINS':
      return !String(memberVal).toLowerCase().includes(String(ruleVal).toLowerCase());
    
    // Number operators
    case '=':
      return Number(memberVal) === Number(ruleVal);
    
    case '!=':
      return Number(memberVal) !== Number(ruleVal);
    
    case '<':
      return Number(memberVal) < Number(ruleVal);
    
    case '<=':
      return Number(memberVal) <= Number(ruleVal);
    
    case '>':
      return Number(memberVal) > Number(ruleVal);
    
    case '>=':
      return Number(memberVal) >= Number(ruleVal);
    
    case 'BETWEEN':
      if (!Array.isArray(ruleVal) || ruleVal.length !== 2) return false;
      const numVal = Number(memberVal);
      const min = Number(ruleVal[0]);
      const max = Number(ruleVal[1]);
      return numVal >= min && numVal <= max;
    
    default:
      console.warn(`Unknown operator: ${operator}`);
      return false;
  }
}

/**
 * Field mapping configuration
 * Maps rule field names to actual member object property names
 * Add new mappings here as needed
 */
const FIELD_MAPPINGS: Record<string, string | string[]> = {
  team: 'team',
  house: ['house', 'house_id'], // Try multiple possible field names
};

/**
 * Gets the member field value from a member object
 * Supports field mapping and nested field access using dot notation
 * 
 * @param member - The member object
 * @param field - The rule field name (will be mapped to actual member property)
 * @returns The field value or undefined if not found
 */
export function getMemberFieldValue(member: any, field: string): any {
  if (!member || !field) return undefined;
  
  // Check if field has a mapping
  const mappedField = FIELD_MAPPINGS[field];
  
  if (mappedField) {
    // If mapping is an array, try each field until one is found
    if (Array.isArray(mappedField)) {
      for (const mapped of mappedField) {
        const value = getNestedValue(member, mapped);
        if (value != null) return value;
      }
      return undefined;
    } else {
      // Single mapping
      return getNestedValue(member, mappedField);
    }
  }
  
  // No mapping, try direct access or dot notation
  return getNestedValue(member, field);
}

/**
 * Helper function to get nested value from object using dot notation
 * 
 * @param obj - The object to traverse
 * @param path - The path (supports dot notation for nested fields)
 * @returns The value or undefined if not found
 */
function getNestedValue(obj: any, path: string): any {
  if (!obj || !path) return undefined;
  
  // Handle dot notation for nested fields
  const fieldParts = path.split('.');
  let value = obj;
  
  for (const part of fieldParts) {
    if (value == null) return undefined;
    value = value[part];
  }
  
  return value;
}

